"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BULK_IMPORT_CSV_HEADERS = void 0;
exports.validateBulkImportRow = validateBulkImportRow;
/**
 * WP-064: Bulk Content Ingestion Pipeline — shared interfaces and validation.
 *
 * Defines the CSV/JSON row shape for bulk kit/activity import, the result
 * shape, and a per-row validation function that checks required fields,
 * activity type, config JSON, content tier, theme, subject, and the
 * safeguarding review gate (reusing kitRequiresSafeguardingReview from
 * WP-057 — single source of truth, not duplicated).
 */
const activityTypes_1 = require("../curriculum/activityTypes");
const themes_1 = require("../curriculum/themes");
/**
 * Ordered CSV header names matching BulkActivityImportRow fields.
 * The CSV header row must match these exactly (case-sensitive).
 */
exports.BULK_IMPORT_CSV_HEADERS = [
    'kitTitle',
    'type',
    'title',
    'configJson',
    'subject',
    'strand',
    'theme',
    'programme',
    'yearGroupMin',
    'yearGroupMax',
    'contentTier',
    'reviewedBy',
    'reviewedOn',
    'instructions',
    'mediaUrls',
];
/** Known valid Subject values for validation. */
const VALID_SUBJECTS = [
    'english', 'maths', 'science', 'history', 'geography',
    'computing', 'art_design', 'design_technology', 'music',
    'pe', 'mfl', 're', 'pshe',
];
/** Known valid ContentTier values for validation. */
const VALID_CONTENT_TIERS = ['basics', 'premium'];
/** Known valid PSHETheme values for validation. */
const VALID_THEMES = [
    'road_safety', 'stranger_danger', 'online_safety', 'healthy_relationships',
    'mental_health', 'anti_bullying', 'fire_safety', 'healthy_eating',
    'british_values', 'protective_behaviours', 'pants_rule', 'rse',
    'first_aid', 'financial_education', 'drug_alcohol_awareness',
    'knife_crime_awareness', 'transition_to_secondary', 'careers_aspirations',
    'young_carers_support', 'bereavement_loss',
];
/** Known valid YearGroup values for validation. */
const VALID_YEAR_GROUPS = [
    'nursery', 'reception', 'y1', 'y2', 'y3', 'y4', 'y5', 'y6',
];
/** Known valid ExternalProgramme values for validation. */
const VALID_PROGRAMMES = [
    'think_road_safety', 'safer_internet_day', 'daily_mile', 'swimming',
    'bikeability_level_1', 'bikeability_level_2', 'bikeability_level_3',
    'crucial_crew', 'nspcc_pants', 'red_cross_first_aid',
    'fire_service_visit', 'forest_school', 'young_carers_network',
];
/**
 * Validates a single bulk import row.
 *
 * Checks:
 * - kitTitle is required (non-blank)
 * - type is required and a valid ActivityType
 * - title is required (non-blank)
 * - configJson is valid JSON
 * - contentTier (if present) is a valid ContentTier
 * - theme (if present) is a valid PSHETheme
 * - subject (if present) is a valid Subject
 * - programme (if present) is a valid ExternalProgramme
 * - yearGroupMin/yearGroupMax (if present) are valid YearGroups
 * - Safeguarding gate: if kitRequiresSafeguardingReview({ contentTier, theme })
 *   returns true, reviewedBy and reviewedOn must be non-blank
 *
 * @param row - The row to validate
 * @param rowIndex - 1-based row number within the batch (for error reporting)
 * @returns { valid: boolean, errors: string[] }
 */
function validateBulkImportRow(row, _rowIndex) {
    const errors = [];
    // kitTitle required
    if (!row.kitTitle || !row.kitTitle.trim()) {
        errors.push('kitTitle is required');
    }
    // type required
    if (!row.type || !row.type.trim()) {
        errors.push('type is required');
    }
    else if (!(0, activityTypes_1.isActivityType)(row.type)) {
        errors.push(`type must be a valid ActivityType, got "${row.type}"`);
    }
    // title required
    if (!row.title || !row.title.trim()) {
        errors.push('title is required');
    }
    // configJson must be valid JSON
    if (!row.configJson || !row.configJson.trim()) {
        errors.push('configJson is required');
    }
    else {
        try {
            JSON.parse(row.configJson);
        }
        catch {
            errors.push('configJson is not valid JSON');
        }
    }
    // contentTier (optional) must be valid if present
    if (row.contentTier && row.contentTier.trim() && !VALID_CONTENT_TIERS.includes(row.contentTier)) {
        errors.push(`contentTier must be one of: ${VALID_CONTENT_TIERS.join(', ')}, got "${row.contentTier}"`);
    }
    // theme (optional) must be valid if present
    if (row.theme && row.theme.trim() && !VALID_THEMES.includes(row.theme)) {
        errors.push(`theme must be a valid PSHETheme, got "${row.theme}"`);
    }
    // subject (optional) must be valid if present
    if (row.subject && row.subject.trim() && !VALID_SUBJECTS.includes(row.subject)) {
        errors.push(`subject must be a valid Subject, got "${row.subject}"`);
    }
    // programme (optional) must be valid if present
    if (row.programme && row.programme.trim() && !VALID_PROGRAMMES.includes(row.programme)) {
        errors.push(`programme must be a valid ExternalProgramme, got "${row.programme}"`);
    }
    // yearGroupMin/yearGroupMax (optional) must be valid if present
    if (row.yearGroupMin && row.yearGroupMin.trim() && !VALID_YEAR_GROUPS.includes(row.yearGroupMin)) {
        errors.push(`yearGroupMin must be a valid YearGroup, got "${row.yearGroupMin}"`);
    }
    if (row.yearGroupMax && row.yearGroupMax.trim() && !VALID_YEAR_GROUPS.includes(row.yearGroupMax)) {
        errors.push(`yearGroupMax must be a valid YearGroup, got "${row.yearGroupMax}"`);
    }
    // Safeguarding gate — reuse kitRequiresSafeguardingReview from WP-057
    const contentTier = row.contentTier || 'basics';
    const theme = row.theme;
    if ((0, themes_1.kitRequiresSafeguardingReview)({ contentTier, theme })) {
        if (!row.reviewedBy || !row.reviewedBy.trim()) {
            errors.push('safeguarding-themed premium content requires reviewedBy');
        }
        if (!row.reviewedOn || !row.reviewedOn.trim()) {
            errors.push('safeguarding-themed premium content requires reviewedOn');
        }
    }
    return { valid: errors.length === 0, errors };
}
